/**
 * BriefNarrative — markdown-lite renderer for AI/worker-generated briefs
 * (# / ## headers, - bullets, **bold**, _italics_ stripped). Serif body per
 * the design system's "briefing narratives" role. No markdown dependency.
 */

import { Fragment, ReactNode } from "react";

function inline(text: string, key: number): ReactNode {
  // **bold** → <strong>, strip stray underscores/backticks
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <Fragment key={key}>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-text">{p}</strong>
        ) : (
          <Fragment key={i}>{p.replace(/^_|_$/g, "")}</Fragment>
        ),
      )}
    </Fragment>
  );
}

export default function BriefNarrative({ text }: { text: string }) {
  const lines = text.replace(/\r/g, "").split("\n");
  const out: ReactNode[] = [];
  let bullets: string[] = [];
  let k = 0;

  const flushBullets = () => {
    if (!bullets.length) return;
    out.push(
      <ul key={k++} className="list-disc list-inside space-y-1 text-body font-serif text-text">
        {bullets.map((b, i) => (
          <li key={i}>{inline(b, i)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushBullets(); continue; }
    if (line.startsWith("## ")) {
      flushBullets();
      out.push(<h3 key={k++} className="text-h3 font-sans text-accent mt-4 mb-1">{line.slice(3)}</h3>);
    } else if (line.startsWith("# ")) {
      flushBullets();
      out.push(<h2 key={k++} className="text-h2 font-serif text-text mt-2 mb-1">{line.slice(2)}</h2>);
    } else if (/^[-*•]\s+/.test(line)) {
      bullets.push(line.replace(/^[-*•]\s+/, ""));
    } else {
      flushBullets();
      out.push(<p key={k++} className="text-body font-serif text-text-muted leading-relaxed">{inline(line, k)}</p>);
    }
  }
  flushBullets();
  return <div className="space-y-1.5 max-w-prose">{out}</div>;
}
